/**
 * F-132: /me/categories editor.
 * Browse the taxonomy (paginated 25/page), pick categories, toggle primary,
 * enforce max 5 secondary. PUT replaces the whole set.
 */
import { useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/layout';
import {
  Button,
  Card,
  Icon,
  Input,
  Spinner,
  toast,
} from '@/components/atoms';
import { catalog, specialists as api } from '@/api';
import type { CategoryRow } from '@/api/specialists';
import type { Category } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

const PAGE_SIZE = 25;
const MAX_SECONDARY = 5;

export function MyCategoriesPage(): JSX.Element {
  const [picked, setPicked] = useState<CategoryRow[] | null>(null);
  const [taxonomy, setTaxonomy] = useState<Category[] | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.fetchMyCategories(), catalog.fetchCategories()])
      .then(([mine, all]) => {
        setPicked(mine.results);
        // Flatten parent + children. The catalog endpoint returns tree.
        const flat: Category[] = [];
        for (const c of all.results) {
          flat.push(c);
          for (const child of c.children ?? []) flat.push(child);
        }
        setTaxonomy(flat);
      })
      .catch((err) => {
        logger.error('categories load failed', err);
        setPicked([]);
        setTaxonomy([]);
      });
  }, []);

  const filtered = useMemo(() => {
    if (!taxonomy) return [];
    const q = query.trim().toLowerCase();
    if (!q) return taxonomy;
    return taxonomy.filter(
      (c) => c.name_ua.toLowerCase().includes(q) || c.slug.includes(q),
    );
  }, [taxonomy, query]);

  const visible = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const persist = async (next: CategoryRow[]): Promise<void> => {
    setBusy(true);
    try {
      const resp = await api.setMyCategories(
        next.map((r) => ({ slug: r.slug, is_primary: r.is_primary })),
      );
      setPicked(resp.results);
    } catch (err) {
      logger.error('categories save failed', err);
      toast.error('Не вдалось зберегти');
    } finally {
      setBusy(false);
    }
  };

  const add = (slug: string, name_ua: string): void => {
    if (!picked) return;
    if (picked.some((p) => p.slug === slug)) return;
    const secondaryCount = picked.filter((p) => !p.is_primary).length;
    if (secondaryCount >= MAX_SECONDARY) {
      toast.error(`Максимум ${MAX_SECONDARY} додаткових розділів`);
      return;
    }
    void persist([...picked, { slug, name_ua, is_primary: false }]);
  };

  const remove = (slug: string): void => {
    if (!picked) return;
    void persist(picked.filter((p) => p.slug !== slug));
  };

  const makePrimary = (slug: string): void => {
    if (!picked) return;
    void persist(picked.map((p) => ({ ...p, is_primary: p.slug === slug })));
  };

  if (picked === null || taxonomy === null) {
    return (
      <Layout title="Розділи">
        <Spinner />
      </Layout>
    );
  }

  return (
    <Layout title="Категорії">
      <div className={styles.list}>
        {picked.length === 0 ? (
          <Card>Поки нічого не обрано.</Card>
        ) : (
          picked.map((p) => (
            <Card key={p.slug}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong style={{ flex: 1 }}>
                  {p.is_primary ? '★ ' : ''}
                  {p.name_ua}
                </strong>
                {!p.is_primary ? (
                  <Button variant="ghost" onClick={() => makePrimary(p.slug)}>
                    Зробити основною
                  </Button>
                ) : null}
                <Button variant="ghost" onClick={() => remove(p.slug)}>
                  <Icon name="x" size={16} />
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>

      <Input
        placeholder="Пошук розділу"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setPage(0);
        }}
        style={{ marginTop: 16 }}
      />

      <div className={styles.list}>
        {visible.map((c) => (
          <Card key={c.slug} interactive onClick={() => add(c.slug, c.name_ua)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Icon name="plus" size={16} />
              <span>{c.name_ua}</span>
            </span>
          </Card>
        ))}
      </div>

      {totalPages > 1 ? (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
          <Button variant="ghost" disabled={page === 0} onClick={() => setPage(page - 1)}>
            ← попередня
          </Button>
          <span style={{ alignSelf: 'center' }}>
            {page + 1} / {totalPages}
          </span>
          <Button
            variant="ghost"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            наступна →
          </Button>
        </div>
      ) : null}

      {busy ? <Spinner /> : null}
    </Layout>
  );
}
